import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AddCircle } from 'iconsax-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PButtonGroup } from '../ButtonGroup';
import { PCheckboxGroup } from '../Checkboxes';
import { PRadio } from '../Radio';
import { PSwitch } from '../Switch';
import { POutlinedField } from '../TextField';
const staticSoftErrorPreviewButtonSx = (theme) => {
    const backgroundColor = alpha(theme.palette.error.main, 0.08);
    const color = theme.palette.error[theme.palette.mode === 'light' ? 'darker' : 'lighter'];
    return {
        pointerEvents: 'none',
        cursor: 'default',
        backgroundColor,
        color,
        '&:hover, &:focus, &:active': {
            backgroundColor,
            color,
            boxShadow: 'none'
        }
    };
};
const staticOutlinedInheritPreviewButtonSx = (theme) => {
    const borderColor = theme.palette.grey['500Transparent'][32];
    return {
        pointerEvents: 'none',
        cursor: 'default',
        borderColor,
        '&:hover, &:focus, &:active': {
            backgroundColor: 'transparent',
            borderColor,
            color: 'inherit',
            boxShadow: 'none',
            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                color: theme.palette.primary.darker
            }
        }
    };
};
const AttendeePreviewOutlinedField = ({ type = 'text', children }) => (_jsx(Box, { sx: {
        '& > div': {
            minHeight: type === 'multiline' ? 135 : 45,
            height: type === 'multiline' ? 135 : 45
        }
    }, children: _jsx(POutlinedField, { type: type, children: children }) }));
const renderSubFieldPreview = (subField) => {
    const optionLabels = subField.options?.map((option) => option.label) || [];
    switch (subField.fieldType) {
        case 'multiline':
            return (_jsx(AttendeePreviewOutlinedField, { type: 'multiline', children: subField.label }, subField.id));
        case 'dropdown':
        case 'companyDropdown':
        case 'pickupLocation':
        case 'multi-select':
            return (_jsx(AttendeePreviewOutlinedField, { type: 'select', children: subField.label }, subField.id));
        case 'radio':
            return (_jsx(PRadio, { label: subField.label, options: optionLabels.length > 0 ? optionLabels : ['Option 1'] }, subField.id));
        case 'choice':
            return (_jsx(PButtonGroup, { label: subField.label, options: optionLabels.length > 0 ? optionLabels : ['Option 1'] }, subField.id));
        case 'checkbox-group':
            return (_jsx(PCheckboxGroup, { label: subField.label, options: optionLabels.length > 0 ? optionLabels : ['Option 1'] }, subField.id));
        case 'toggle':
            return _jsx(PSwitch, { label: subField.label }, subField.id);
        case 'date':
        case 'time':
        case 'email':
        case 'phone':
        case 'text':
        default:
            return (_jsx(AttendeePreviewOutlinedField, { type: 'text', children: subField.label }, subField.id));
    }
};
const AdditionalAttendeesPreview = ({ field }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const subFields = useMemo(() => {
        return [...(field.additionalAttendeeFields || [])].sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0));
    }, [field.additionalAttendeeFields]);
    return (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: theme.palette.common.grey8,
            border: `1px solid ${theme.palette.common.grey24}`
        }, children: [_jsx(Typography, { variant: 'subtitle2', fontWeight: 500, color: 'text.primary', children: field.label || t('events.additionalAttendeesPreset.title') }), _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap'
                }, children: [_jsx(Typography, { variant: 'body2', fontWeight: 500, color: 'text.primary', children: t('events.additionalAttendeesPreset.previewPerson', { number: 1 }) }), _jsx(Button, { variant: 'outlined', size: 'small', color: 'inherit', children: t('events.additionalAttendeesPreset.previewHideDetails') })] }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [subFields.map((subField) => renderSubFieldPreview(subField)), _jsx(Button, { variant: 'soft', color: 'error', size: 'small', sx: staticSoftErrorPreviewButtonSx(theme), children: t('events.additionalAttendeesPreset.previewRemovePerson') })] }), _jsx(Box, { children: _jsx(Button, { variant: 'outlined', size: 'small', color: 'inherit', startIcon: _jsx(AddCircle, { size: 20, color: theme.palette.primary.darker }), sx: staticOutlinedInheritPreviewButtonSx(theme), children: t('events.additionalAttendeesPreset.previewAddPerson') }) })] }));
};
export default AdditionalAttendeesPreview;
